import SelectRoom from '../selectRoom'
import { Col, Empty, Form, Row } from 'antd'
import { Button, DatePickerRange, SpinLoading } from 'components/atoms'
import { NO_DATA } from 'constants/common'
import { SEARCH_BOOKING_QUERY } from 'constants/query'
import { useOnlineStatus } from 'detectConnectServer'
import { EColorButton, ETypeStatus } from 'enums'
import { useProfile } from 'hooks'
import { IOptionItem, IStatus } from 'interfaces'
import { CFilterExam } from 'pages/exam/constants/CFilterExam'
import { useExamContext } from 'pages/exam/context'
import { EExamStatus } from 'pages/patientBooking/enums'
import { IBooking, IBookingQuery } from 'pages/patientBooking/interfaces'
import useSearchBooking from 'pages/patientBooking/logic/useSearchBooking'
import useClinicOptionRooms from 'pages/patientReceive/logic/useClinicOptionRooms'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useBookingServices } from 'services'
import { ESocketEventName, socketIoService } from 'socket'
import styled from 'styled-components'
import { StyleCardBody, StyledSearchInput } from 'styles/styledGlobal'
import { debounce, formatDate } from 'utils'
import { NOT_NUMBER } from 'utils/regularExpressions'

const StyleContentPayment = styled(StyleCardBody)`
  padding: 8px;
  margin-bottom: 16px;
`
export const StylePatientItem = styled(StyleCardBody)<{
  $active?: boolean
  $color?: EColorButton
}>`
  display: flex;
  padding: 8px;
  margin-top: 8px;
  cursor: pointer;
  ${({ $active }) =>
    $active
      ? `
    border: 1px solid var(--color-primary-108);
    background: var(--color-primary-103);
    .booking-order {
      // background: var(--color-primary-108);
      color: white;
    }
    `
      : ''}
  &:hover {
    border: 1px solid var(--color-primary-108);
  }
`
export const StyleBookingOrder = styled(Col)<{ $color?: EColorButton }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`

export const StyleBookingInfo = styled(Col)`
  display: flex;
  flex-direction: column;
  flex: 1;
  line-height: 22px;
  .patient-name {
    font-weight: 700;
    font-size: 12px;
  }
  .patient-code {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .separator {
      width: 1px;
      height: 12px;
      background-color: var(--color-gray-102);
    }
  }
  .room {
    font-weight: 600;
  }
`
const StyledColFilter = styled(Col)`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
`
const StyledSelectRoom = styled(SelectRoom)`
  margin-bottom: 20px;
`
const StyledButton = styled(Button)`
  min-width: 110px;
`

interface IProps {
  bookingIdSelected?: number
  examStatusDefault?: Array<IStatus>
  examSelectedDefault?: Array<IStatus>
  rootPath?: string
  query?: IBookingQuery
  typeStatus?: ETypeStatus
  allowClearRoomFilter?: boolean
  defaultRoomFilter?: boolean
  isChangeInProgress?: boolean
  roomDefault?: number
  onProcessBooking?: (booking: IBooking) => void
  onGetBookings?: (bookings: Array<IBooking>) => void
  statusChange?: Array<IStatus>
}

const ListPatientBooking = (props: IProps, ref: any) => {
  const {
    bookingIdSelected,
    examStatusDefault,
    rootPath,
    query,
    typeStatus,
    defaultRoomFilter,
    examSelectedDefault,
    onGetBookings,
    onProcessBooking,
    statusChange
  } = props
  const { reloadBookingDetail } = useExamContext()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [statusSelected, setStatusSelected] = useState<Array<IStatus>>(
    examSelectedDefault || examStatusDefault || []
  )
  const { profile } = useProfile()
  const { optionRooms } = useClinicOptionRooms()
  const { countBookings } = useBookingServices()
  const [roomSelected, setRoomSelected] = useState<IOptionItem | null>(null)
  const { searchBookings, bookings } = useSearchBooking()
  const queryRef = useRef<IBookingQuery>(query || SEARCH_BOOKING_QUERY)
  const isOnline = useOnlineStatus()
  const [numberCountBookings, setCountBookings] = useState<any>()
  useImperativeHandle(ref, () => {
    return {
      reloadSearchBookings: () => {
        searchBookings(queryRef.current)
      }
    }
  })
  const clinicUserDoctor = optionRooms.filter(
    (clinicUserId) =>
      clinicUserId?.data?.doctor.ClinicUserID === profile?.my_clinic_user.id
  )
  const status = (
    statusSelected?.length > 0 ? statusSelected : examStatusDefault
  )?.map((status) => status.value)
  const newQuery = {
    ...queryRef.current,
    query: {
      ...queryRef.current?.query,
      room_id: roomSelected?.data?.id ?? roomSelected?.value,
      [typeStatus as string]: status
    }
  }
  queryRef.current = newQuery

  useEffect(() => {
    if (statusChange) {
      if (statusSelected.length === examSelectedDefault?.length) return
      setStatusSelected(statusChange)
      reloadBookingDetail()
    }
  }, [statusChange])

  useEffect(() => {
    searchBookings(newQuery)
    console.log('[ListPatientBooking] isOnline: ', isOnline)
    socketIoService.subscribeBookingUpdateStatus(() => {
      searchBookings({
        ...newQuery,
        query: {
          ...newQuery.query,
          [typeStatus as string]: statusSelected?.map((status) => status.value)
        }
      })
    })
    return () => {
      socketIoService.unsubscribeEvent(
        ESocketEventName.MANAGE_BOOKING_UPDATE_STATUS_TOPIC
      )
    }
  }, [statusSelected, isOnline])
  useEffect(() => {
    if (optionRooms?.length > 0 && defaultRoomFilter) {
      setRoomSelected(clinicUserDoctor[0])
    }
  }, [defaultRoomFilter, optionRooms])
  useEffect(() => {
    if (onGetBookings) onGetBookings(bookings)
  }, [bookings])

  useEffect(() => {
    countBookings({
      query: {
        from: queryRef?.current?.query?.from,
        to: queryRef?.current?.query?.to,
        room_id: Number(roomSelected?.data?.id)
      }
    }).then((res) => {
      if (res._successfully) setCountBookings(res.data)
    })
    if (roomSelected || !defaultRoomFilter) {
      searchBookings(newQuery)
    }
  }, [
    roomSelected,
    statusChange,
    statusSelected,
    queryRef?.current?.query?.from,
    queryRef?.current?.query?.to
  ])

  const handleSearchListClinic = () => {
    setStatusSelected(examStatusDefault || [])
    searchBookings({
      ...newQuery,
      query: {
        ...queryRef.current.query,
        room_id: undefined
      }
    })
  }
  const handleClick = (booking: IBooking) => {
    navigate(`${rootPath}/${booking?.booking_id}`)
  }

  const handleDoubleClick = (booking: IBooking) => {
    if (onProcessBooking) onProcessBooking(booking)
    if (statusSelected.length === examSelectedDefault?.length) return
    else {
      if (
        booking?.exam_status === EExamStatus.Waiting ||
        booking?.exam_status === EExamStatus.DataCollection
      ) {
        navigate(`${rootPath}/${booking?.booking_id}`)
        setStatusSelected([CFilterExam.InProgress])
      } else return
    }
  }
  const handleOnChangeSearch = useCallback(
    debounce((inputSearch: string) => {
      if (inputSearch?.length < 3) {
        inputSearch = ''
      }
      const isNumber = NOT_NUMBER.test(inputSearch)
      const newQuery = {
        ...queryRef.current,
        query: {
          ...queryRef.current.query,
          phone_number: isNumber ? inputSearch || undefined : undefined,
          patient_name: isNumber ? undefined : inputSearch || undefined
        }
      }
      queryRef.current = newQuery
      searchBookings(newQuery)
    }, 500),
    []
  )

  const handleOnChangeRoom = (room: IOptionItem) => {
    setRoomSelected(room)
  }

  const handleOnChangeDate = (rangeDate: any) => {
    const { from, to } = rangeDate
    let fromTime = undefined
    let toTime = undefined
    if (to) {
      toTime = new Date(to).getTime()
    }
    if (from) {
      fromTime = new Date(from).getTime()
    }
    const newQuery = {
      ...queryRef.current,
      room_id: 1,
      query: { ...queryRef.current.query, from: fromTime, to: toTime }
    }
    queryRef.current = newQuery
    searchBookings(newQuery)
  }
  const handleOnChangeStatus = (isActive: boolean, status: IStatus) => {
    setStatusSelected([status])
    if (roomSelected) return
    searchBookings({
      ...newQuery,
      query: {
        ...queryRef.current.query,
        [typeStatus as string]: [status.value]
      }
    })
  }
  const isActiveTab = (value: any): boolean => {
    return statusSelected?.some((status) => status.value === value)
  }
  const onClickAll = () => {
    setStatusSelected(examStatusDefault || [])
    searchBookings({
      ...newQuery,
      query: {
        ...queryRef.current.query,
        [typeStatus as string]: examStatusDefault?.map((status) => status.value)
      }
    })
  }
  const getColorByStatus = (status: EExamStatus) => {
    const eStatus = examStatusDefault?.find((item) => item?.value === status)
    return eStatus?.color
  }

  const getStatusCount = (statusValue: string) => {
    switch (statusValue) {
      case 'waiting':
        return numberCountBookings?.total_waiting_exam ?? 0
      case 'in_progress':
        return numberCountBookings?.total_in_exam ?? 0
      case 'data_collection':
        return numberCountBookings?.total_collection_data ?? 0
      case 'finished':
        return numberCountBookings?.total_finshed ?? 0
      case 'canceled':
        return numberCountBookings?.total_canceled ?? 0
      default:
        return 0
    }
  }

  return (
    <Form>
      <StyleContentPayment>
        <DatePickerRange
          onChange={handleOnChangeDate}
          defaultValue={{ from: new Date(), to: new Date() }}
        />
        <StyledSelectRoom
          allowClear={true}
          title={t('clinic-room')}
          onClear={handleSearchListClinic}
          placeholder="please-choose-info"
          options={optionRooms}
          value={roomSelected}
          onChange={(_: any, op: IOptionItem) => handleOnChangeRoom(op)}
        />

        <Row gutter={12} className="filter">
          <StyledColFilter span={12} className="filter-all">
            <StyledButton
              color={EColorButton.Green}
              btnOutline={statusSelected?.length !== examStatusDefault?.length}
              onClick={onClickAll}>
              {t('all')}
              {numberCountBookings?.total_items > 0 &&
                `(${numberCountBookings?.total_items})`}
            </StyledButton>
          </StyledColFilter>
          {examStatusDefault?.map((status: any, index) => (
            <StyledColFilter
              span={12}
              key={status.value}
              onClick={() => {
                handleOnChangeStatus(isActiveTab(status.value), status)
              }}>
              <StyledButton
                btnOutline={!isActiveTab(status.value)}
                color={
                  (status?.color as EColorButton) || EColorButton.BlueTrace
                }>
                {t(status?.label)}
                {getStatusCount(status.value) > 0 &&
                  `(${getStatusCount(status.value)})`}
              </StyledButton>
            </StyledColFilter>
          ))}
        </Row>
      </StyleContentPayment>
      <StyleContentPayment>
        <StyledSearchInput
          size="small"
          style={{ height: 40, width: '100%' }}
          placeholder={t('please-enter-patient-name')}
          suffix={<img src="/images/ic-search.svg" alt="" />}
          onChange={(e) => handleOnChangeSearch(e?.target?.value)}
        />
        {bookings?.length === 0 ? (
          <Empty
            description={t('no-data-patient')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <SpinLoading loading={false}>
            <div style={{ maxHeight: '1370px', overflowY: 'auto' }}>
              {bookings
                ?.sort((a, b) => a.booking_no.localeCompare(b.booking_no))
                .map((booking) => (
                  <PatientItem
                    key={booking.booking_id}
                    booking={booking}
                    color={
                      getColorByStatus(
                        (booking as any)?.[typeStatus as string]
                      ) as any
                    }
                    active={bookingIdSelected === booking.booking_id}
                    onClick={() => handleClick(booking)}
                    onDoubleClick={() => handleDoubleClick(booking)}
                    roomName={roomSelected?.data?.name as any}
                  />
                ))}
            </div>
          </SpinLoading>
        )}
      </StyleContentPayment>
    </Form>
  )
}

export default forwardRef(ListPatientBooking)

interface PatientItemProps {
  booking?: IBooking
  active?: boolean
  color?: EColorButton
  onClick?: () => void
  onDoubleClick?: () => void
  roomName?: string
}
export const PatientItem = (props: PatientItemProps) => {
  const { t } = useTranslation('list-patient-booking')
  const { color, booking, active, onClick, onDoubleClick } = props
  const clickTimeout = useRef<NodeJS.Timeout | null>(null)
  const handleClick = () => {
    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current)
    }
    clickTimeout.current = setTimeout(() => {
      if (onClick) {
        onClick()
      }
    }, 200)
  }

  const handleDoubleClick = () => {
    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current)
    }
    if (onDoubleClick) {
      onDoubleClick()
    }
  }
  return (
    <StylePatientItem
      onClick={handleClick}
      $active={active}
      $color={color}
      onDoubleClick={handleDoubleClick}>
      <StyleBookingOrder className="booking-order" $color={color}>
        <Button color={color}> {booking?.booking_no}</Button>
      </StyleBookingOrder>
      <StyleBookingInfo className="patient">
        <div
          className="patient-name"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
          <span>{booking?.patient?.full_name}</span>
          {booking?.priority === 1 && (
            <span
              style={{
                color: 'red',
                fontSize: '10px',
                fontWeight: 'bold',
                border: '1px solid red',
                borderRadius: '5px',
                padding: '0px 2px 0px 2px'
              }}>
              {t(`priority-${booking?.priority}`).toUpperCase()}
            </span>
          )}
        </div>
        <div className="patient-code">
          <span>
            {t('YYYY')}: {booking?.patient?.year || NO_DATA}
          </span>
          <span className="separator" />
          <span>{formatDate(booking?.from, 'HH:mm DD/MM/YYYY')}</span>
        </div>
        <div className="patient-booking-price">
          {booking?.patient_remaining_amount}
        </div>
      </StyleBookingInfo>
    </StylePatientItem>
  )
}
